/**
 * External/programmatic control for spreadsheet column and row operations.
 * Local-only: mutate Univer sheet and in-memory state, no backend.
 */

import { PRIMARY_COLUMN_NAMES } from '../../api/spreadsheet';
import type { Column } from '../../api/spreadsheet';
import type { SpreadsheetColumnTypeMap } from '../../constants/spreadsheetColumnTypes';
import {
  MAX_SPREADSHEET_COLUMNS,
  MAX_SPREADSHEET_ROWS
} from './spreadsheetConstants';
import type { HeaderState } from './spreadsheetTypes';
import { normalizeCellValue, normalizeColumnIndices } from './spreadsheetUtils';

/** Context required for local-only column/row ops. Pass refs from Spreadsheet/Univer setup. */
export interface LocalColumnContext {
  univerAPI: {
    executeCommand: (
      id: string,
      params?: {
        range?: {
          startRow?: number;
          endRow?: number;
          startColumn?: number;
          endColumn?: number;
        };
        value?: Record<number, Record<number, unknown>>;
      }
    ) => void;
    getActiveWorkbook?: () => {
      getActiveSheet?: () => {
        getSheet?: () => {
          getConfig?: () => { rowCount?: number };
          getCellValueOnly?: (row: number, col: number) => unknown;
        };
      };
    } | null;
  };
  headerStateRef: { current: HeaderState | null };
  skipInsertColBeforeRef: { current: boolean };
  skipInsertColCreateRef: { current: boolean };
  /** Required for appendRowsToSpreadsheetLocal to populate cell values. */
  cellValueToUniverCell?: (
    rawVal: string,
    options: { colType?: string }
  ) => { v?: unknown; p?: unknown; t: number; s: Record<string, unknown> };
}

/**
 * Insert a column at index, local-only (Univer + state). No backend.
 * Validates primary column protection and max columns.
 */
export function insertColumnLocal(
  ctx: LocalColumnContext,
  colIdx: number,
  options?: { name?: string; colType?: string; description?: string }
): void {
  const state = ctx.headerStateRef.current;
  if (!state?.columnNamesByIndex || !ctx.univerAPI) return;

  const { columnNamesByIndex, columns, getUniqueColumnName: getUnique } = state;
  if (columnNamesByIndex.length >= MAX_SPREADSHEET_COLUMNS) return;

  // Primary columns cannot be inserted to the left of the primary columns
  const rightmostPrimary = Math.max(
    -1,
    ...PRIMARY_COLUMN_NAMES.map((n) => columnNamesByIndex.indexOf(n)).filter(
      (i) => i >= 0
    )
  );
  if (rightmostPrimary >= 0 && colIdx <= rightmostPrimary) return;

  const baseName = options?.name ?? 'Column ' + colIdx;
  const name = getUnique(baseName, columnNamesByIndex);
  columnNamesByIndex.splice(colIdx, 0, name);
  if (columns)
    columns.splice(colIdx, 0, {
      col_name: name,
      col_idx: colIdx,
      col_type: options?.colType ?? 'str',
      description: options?.description ?? ''
    } as Column);
  if (columns) normalizeColumnIndices(columns);

  ctx.skipInsertColBeforeRef.current = true;
  ctx.skipInsertColCreateRef.current = true;
  ctx.univerAPI.executeCommand('sheet.command.insert-col', {
    range: { startColumn: colIdx, endColumn: colIdx }
  });
  state.refreshHeaderBar?.();
}

/**
 * True when every cell in the row is empty after the same normalization Univer
 * uses elsewhere (`normalizeCellValue` handles primitives and rich-cell objects).
 */
function rowIsBlankUniver(
  sheet: { getCellValueOnly?: (row: number, col: number) => unknown },
  row: number,
  colCount: number
): boolean {
  if (typeof sheet.getCellValueOnly !== 'function') return false;
  for (let c = 0; c < colCount; c++) {
    const raw = sheet.getCellValueOnly(row, c);
    const s = String(normalizeCellValue(raw ?? '')).trim();
    if (s !== '') return false;
  }
  return true;
}

/**
 * Counts consecutive blank rows from the bottom (must run before insert/remove mutations).
 */
function countTrailingBlankRows(
  ctx: LocalColumnContext,
  rowCount: number
): number {
  if (rowCount <= 0 || !ctx.univerAPI) return 0;
  const colCount = ctx.headerStateRef.current?.columnNamesByIndex?.length ?? 0;
  if (colCount <= 0) return 0;

  const sheet = ctx.univerAPI
    .getActiveWorkbook?.()
    ?.getActiveSheet?.()
    ?.getSheet?.();
  if (!sheet || typeof sheet.getCellValueOnly !== 'function') return 0;

  let trailing = 0;
  for (let r = rowCount - 1; r >= 0; r--) {
    if (!rowIsBlankUniver(sheet, r, colCount)) break;
    trailing++;
  }
  return trailing;
}

/**
 * Append rows locally: create missing columns via insertColumnLocal, add rows via insertRowLocal.
 * Optionally populates cell values when cellValueToUniverCell is in ctx.
 * Returns the number of rows "added".
 */
export function appendRowsToSpreadsheetLocal(
  ctx: LocalColumnContext,
  uploadedRows: Record<string, unknown>[],
  customColumnTypes?: SpreadsheetColumnTypeMap
): number {
  const state = ctx.headerStateRef.current;
  if (!state?.columnNamesByIndex || !ctx.univerAPI) return 0;
  const existingColNames = new Set(state.columnNamesByIndex);
  const columnNamesInUpload = new Set<string>();
  for (const row of uploadedRows) {
    for (const name of Object.keys(row)) {
      if (name.trim().length > 0) columnNamesInUpload.add(name.trim());
    }
  }
  const newColumnNames = Array.from(columnNamesInUpload).filter(
    (n) => !existingColNames.has(n)
  );
  for (const colName of newColumnNames) {
    const colIdx = ctx.headerStateRef.current?.columnNamesByIndex?.length ?? 0;
    const colType = customColumnTypes?.[colName] ?? 'str';
    insertColumnLocal(ctx, colIdx, { name: colName, colType });
  }
  const numRows = uploadedRows.length;
  if (numRows === 0) return 0;

  // Check limit synchronously so caller can show correct feedback
  const sheet = ctx.univerAPI
    .getActiveWorkbook?.()
    ?.getActiveSheet?.()
    ?.getSheet?.();
  const currentRows = sheet?.getConfig?.()?.rowCount ?? 0;

  const trailingBlankRows = countTrailingBlankRows(ctx, currentRows);
  const reusableRows = Math.min(trailingBlankRows, numRows);
  const needInsert = numRows - reusableRows;
  const startReuseRow = currentRows - trailingBlankRows;

  if (currentRows + needInsert > MAX_SPREADSHEET_ROWS) {
    return -1; // Signal limit exceeded (caller shows error)
  }

  const toUniver = ctx.cellValueToUniverCell;
  const columnNamesByIndex =
    ctx.headerStateRef.current?.columnNamesByIndex ?? [];
  requestAnimationFrame(() => {
    if (needInsert > 0) {
      insertRowLocal(ctx, currentRows, needInsert);
    }

    if (toUniver && columnNamesByIndex.length > 0) {
      requestAnimationFrame(() => {
        const value: Record<number, Record<number, unknown>> = {};
        for (let i = 0; i < uploadedRows.length; i++) {
          const univerRow =
            i < reusableRows
              ? startReuseRow + i
              : currentRows + (i - reusableRows);
          const row = uploadedRows[i];
          for (const colName of Object.keys(row)) {
            const trimmed = colName.trim();
            if (!trimmed) continue;
            const colIdx = columnNamesByIndex.indexOf(trimmed);
            if (colIdx < 0) continue;
            const raw = row[colName];
            const strVal = raw != null && raw !== '' ? String(raw) : '';
            const colType = customColumnTypes?.[trimmed] ?? 'str';
            if (!value[univerRow]) value[univerRow] = {};
            value[univerRow][colIdx] = toUniver(strVal, { colType });
          }
        }
        const rows = Object.keys(value).map(Number);
        if (rows.length > 0) {
          const minR = Math.min(...rows);
          const maxR = Math.max(...rows);
          const cols = new Set<number>();
          for (const r of Object.keys(value).map(Number)) {
            for (const c of Object.keys(value[r]!).map(Number)) cols.add(c);
          }
          const colArr = Array.from(cols);
          const minC = Math.min(...colArr);
          const maxC = Math.max(...colArr);
          ctx.univerAPI.executeCommand('sheet.command.set-range-values', {
            range: {
              startRow: minR,
              endRow: maxR,
              startColumn: minC,
              endColumn: maxC
            },
            value
          });
        }
      });
    }
  });
  return numRows;
}

/**
 * Insert row(s) at index, local-only (Univer + state).
 * Uses sheet.command.insert-row; the Univer CommandExecuted handler syncs to backend.
 * Validates max rows.
 */
export function insertRowLocal(
  ctx: LocalColumnContext,
  startRow: number,
  numRows = 1
): void {
  if (!ctx.univerAPI) return;
  const sheet = ctx.univerAPI
    .getActiveWorkbook?.()
    ?.getActiveSheet?.()
    ?.getSheet?.();
  const currentRows = sheet?.getConfig?.()?.rowCount ?? 0;
  if (currentRows + numRows > MAX_SPREADSHEET_ROWS) return;
  const endRow = startRow + numRows - 1;
  ctx.univerAPI.executeCommand('sheet.command.insert-row', {
    range: { startRow, endRow }
  });
}
